import AcxMenu, { AcxMenuItemProps } from "components/UI/Menu/AcxMenu";
import React, { useRef, useState } from "react";
import { useStore } from "utils/useStore";
import SignalsPageStore from "../../Store/SignalsPageStore";
import theme from "Theme/AppTheme";
import {
    CancelPresentationRounded,
    ControlPointDuplicateRounded,
    DashboardCustomizeRounded,
    DeleteRounded,
    DownloadRounded,
    EditRounded,
    HomeRounded,
    HomeWorkRounded,
    MoreHoriz,
    PlayCircleOutlineRounded,
    SendRounded,
    Share,
} from "@mui/icons-material";
import { useCurrentDashboard } from "../../useCurrentDashboard";
import { ReportAccessType } from "models/Reporting/ReportAccessControls";
import { SignalsDashboardType } from "../../SignalsModels";
import { IconButton, useMediaQuery } from "@mui/material";
import { GRID_ARRANGE_MEDIA_QUERY } from "../Layout/grid";
import { DashboardConfirmDelete } from "./DashboardConfirmDelete";
import { DashboardSharingDialog } from "./DashboardSharingDialog";
import { Options } from "react-to-pdf";
import { DashboardPdfDialog } from "./DashboardPdfDialog";
import { DashboardDuplicateDialog } from "./DashboardDuplicateDialog";
import { trackHomePage } from "../../SignalsUtils";
import { IDashboardTenantVisibility } from "../../SignalsModels";
import { useLocation, useNavigate } from "react-router";
import { observer } from "mobx-react";
import {
    defaultDrawerOffset,
    LayoutDrawerStore,
} from "stores/Layout/LayoutDrawerStore";
import CustomizeDashboard from "../CustomizeDashboard";
import AcxDialog from "components/UI/Dialog/AcxDialog";
import AcxButton from "components/UI/AcxButton";
import { DashboardScheduleEmailDialog } from "./DashboardScheduleEmailDialog";

// The menu component for the dashboard, providing options like edit, share, duplicate, set as home, save as PDF, and delete
// toPDF function is passed in from the DashboardBody where the ref to the dashboard content exists
export const DashboardMenu = observer(
    ({ toPDF }: { toPDF: (options?: Options) => Promise<void> }) => {
        const signalsPageStore = useStore(SignalsPageStore);
        const drawerStore = useStore(LayoutDrawerStore);
        const currentDashboard = useCurrentDashboard();
        const location = useLocation();
        const navigate = useNavigate();

        const ref = useRef<HTMLButtonElement>(null);
        const [menuOpen, setMenuOpen] = useState(false);
        const isWideEnoughToArrange = useMediaQuery(GRID_ARRANGE_MEDIA_QUERY);

        const isCustomDashboard =
            currentDashboard?.dashboardType === SignalsDashboardType.Custom;

        const canDeleteDashboard =
            (currentDashboard &&
                currentDashboard.userVisibility === ReportAccessType.Owner &&
                currentDashboard.dashboardIsEditable &&
                !currentDashboard.isPresentationMode &&
                !currentDashboard.isUserHomeDashboard &&
                !currentDashboard.isOrgHomeDashboard &&
                isCustomDashboard) ??
            false;

        // Dialog states
        const [showConfirmDelete, setShowConfirmDelete] = useState(false);
        const [showDupEdit, setShowDupEdit] = useState(false);
        const [savePDFOpen, setSavePDFOpen] = useState(false);
        const [shareDialogOpen, setShareDialogOpen] = useState(false);
        const [showEmailDialog, setShowEmailDialog] = useState(false);
        const [showConfirmUnsubscribe, setShowConfirmUnsubscribe] =
            useState(false);
        const [unsubscribeLoading, setUnsubscribeLoading] = useState(false);
        const closeMenu = () => setMenuOpen(false);

        function toggleCustomizeDashboardDrawer() {
            if (!currentDashboard?.id) {
                return;
            }

            drawerStore.closeAndResetDrawer();
            drawerStore.restoreDefaults();
            drawerStore.setOffsetPixels(defaultDrawerOffset);
            drawerStore.setCustomRootStyles({
                zIndex: theme.zIndex.appBar - 1,
            });
            drawerStore.setContentFactory(() => (
                <CustomizeDashboard
                    dashboardId={currentDashboard!.id}
                    currentName={currentDashboard!.title}
                    dashboard={currentDashboard}
                    // currentPinnedFilter={applicationFiltersStore.getSavedFilterById(
                    //     currentDashboard?.pinnedApplicationFilterId,
                    // )}
                    updateDashboard={currentDashboard.updateDashboard}
                    closeDrawer={() => drawerStore.setOpen(false)}
                />
            ));

            drawerStore.setOpen(true);
        }

        const handleUnsubscribe = async () => {
            if (!currentDashboard?.signalsDashboardSubscriptionId) {
                return;
            }

            try {
                setUnsubscribeLoading(true);
                await signalsPageStore.signalsService.unsubscribeDashboardEmail(
                    currentDashboard.signalsDashboardSubscriptionId,
                );
                currentDashboard.signalsDashboardSubscriptionId = undefined;
                signalsPageStore.messageStore.logMessage(
                    "Successfully unsubscribed.",
                    "success",
                );
                setShowConfirmUnsubscribe(false);
            } catch {
                signalsPageStore.messageStore.logMessage(
                    "Failed to unsubscribe",
                    "error",
                );
            } finally {
                setUnsubscribeLoading(false);
            }
        };

        const handleOpenEmailDialog = () => {
            setShowEmailDialog(true);
            closeMenu();
        };

        const menuItems: Array<AcxMenuItemProps | "Divider"> = [];

        const addEmailMenuItem = () => {
            if (!currentDashboard) {
                return;
            }

            if (currentDashboard.signalsDashboardSubscriptionId) {
                menuItems.push({
                    id: "email-dashboard-unsubscribe-menu-option",
                    props: {
                        onClick: () => {
                            setShowConfirmUnsubscribe(true);
                            closeMenu();
                        },
                    },
                    label: "Unsubscribe",
                    icon: <SendRounded />,
                });
                return;
            }

            menuItems.push({
                id: "email-dashboard-subscribe-menu-option",
                props: {
                    onClick: handleOpenEmailDialog,
                },
                label: "Email",
                icon: <SendRounded />,
            });
        };

        if (currentDashboard?.isPresentationMode) {
            menuItems.push({
                id: "signals-dashboard-present",
                props: {
                    onClick: () => {
                        currentDashboard.setIsPresentationMode(false);
                        closeMenu();
                    },
                },
                label: "Exit Presentation",
                icon: <CancelPresentationRounded />,
            });
        }

        if (currentDashboard && !currentDashboard.isPresentationMode) {
            if (isCustomDashboard && currentDashboard.dashboardIsEditable) {
                menuItems.push({
                    id: "signals-dashboard-edit",
                    props: {
                        onClick: () => {
                            toggleCustomizeDashboardDrawer();
                            closeMenu();
                        },
                    },
                    label: "Edit",
                    icon: <EditRounded />,
                });
            }

            // Arranging is gated on being able to save the dashboard,
            // because the layout is the shared frame everyone sees rather than
            // a personal view like a filter or a selection. It cannot start in
            // a window too narrow to drag and resize.
            if (
                currentDashboard.canEditDashboardLayout &&
                !currentDashboard.isArranging
            ) {
                menuItems.push({
                    id: "signals-dashboard-arrange",
                    secondaryLabel: isWideEnoughToArrange
                        ? undefined
                        : "Needs a wider window",
                    props: {
                        disabled: !isWideEnoughToArrange,
                        onClick: () => {
                            currentDashboard.dashboardLayoutStore.startEditing();
                            closeMenu();
                        },
                    },
                    label: "Arrange cards",
                    icon: <DashboardCustomizeRounded />,
                });
            }

            menuItems.push({
                id: "signals-dashboard-duplicate",
                props: {
                    onClick: () => {
                        setShowDupEdit(true);
                        closeMenu();
                    },
                },
                label: "Duplicate",
                icon: <ControlPointDuplicateRounded />,
            });

            if (!currentDashboard.isUserHomeDashboard) {
                menuItems.push({
                    id: "signals-set-user-homepage",
                    props: {
                        onClick: async () => {
                            closeMenu();
                            try {
                                await signalsPageStore.authStore.homeDashboardStore.updateUserHomeDashboardId(
                                    currentDashboard.id,
                                );
                                trackHomePage(
                                    signalsPageStore.currentDashboard?.id,
                                    signalsPageStore.currentDashboard?.title,
                                    location.pathname,
                                );
                                if (signalsPageStore.userHomeDashboard) {
                                    signalsPageStore.userHomeDashboard.setIsUserHomeDashboard(
                                        false,
                                    );
                                }
                                currentDashboard.setIsUserHomeDashboard(true);
                                navigate(`/app/signals/home`);
                            } catch {
                                signalsPageStore.messageStore.logError(
                                    "Failed to set home dashboard",
                                );
                            }
                        },
                    },
                    label: "Set as home",
                    icon: <HomeRounded />,
                });
            }

            if (currentDashboard.isUserHomeDashboard) {
                menuItems.push({
                    id: "signals-set-user-homepage",
                    props: {
                        onClick: async () => {
                            closeMenu();
                            try {
                                await signalsPageStore.authStore.homeDashboardStore.removeUserHomeDashboardId();
                                currentDashboard.setIsUserHomeDashboard(false);
                                navigate(
                                    `/app/signals/${currentDashboard.route}/${currentDashboard.id}`,
                                );
                            } catch {
                                signalsPageStore.messageStore.logError(
                                    "Failed to remove home dashboard",
                                );
                            }
                        },
                    },
                    label: "Remove as home",
                    icon: <HomeRounded />,
                });
            }

            if (
                !currentDashboard.isOrgHomeDashboard &&
                (currentDashboard.tenantVisibility ===
                    IDashboardTenantVisibility.PubliclyEditable ||
                    currentDashboard.tenantVisibility ===
                        IDashboardTenantVisibility.PubliclyViewable ||
                    !currentDashboard) &&
                signalsPageStore.authStore.homeDashboardStore
                    .canEditOrganizationHomeDashboard
            ) {
                menuItems.push({
                    id: "signals-set-org-homepage",
                    props: {
                        onClick: async () => {
                            closeMenu();
                            try {
                                await signalsPageStore.authStore.homeDashboardStore.updateOrgHomeDashboardId(
                                    currentDashboard.id,
                                );
                                trackHomePage(
                                    signalsPageStore.currentDashboard?.id,
                                    signalsPageStore.currentDashboard?.title,
                                    location.pathname,
                                );
                                if (signalsPageStore.orgHomeDashboard) {
                                    signalsPageStore.orgHomeDashboard.setIsOrgHomeDashboard(
                                        false,
                                    );
                                }
                                currentDashboard.setIsOrgHomeDashboard(true);
                                if (
                                    !signalsPageStore.userHomeDashboard &&
                                    signalsPageStore.authStore
                                        .homeDashboardStore
                                        .userHomeDashboardId !== "reporting"
                                ) {
                                    // If we are setting the org home and we DONT have a user home
                                    // (and the user home is not reporting, since we wouldn't have the computed dashboard in that case)
                                    // then we navigate to the home page. Otherwise we don't (because it will be the user home they go to, not org)
                                    navigate(`/app/signals/home`);
                                }
                            } catch {
                                signalsPageStore.messageStore.logError(
                                    "Failed to set org home dashboard",
                                );
                            }
                        },
                    },
                    label: "Set as org home",
                    icon: <HomeWorkRounded />,
                });
            }

            if (
                currentDashboard.isOrgHomeDashboard &&
                signalsPageStore.authStore.homeDashboardStore
                    .canEditOrganizationHomeDashboard
            ) {
                menuItems.push({
                    id: "signals-set-org-homepage",
                    props: {
                        onClick: async () => {
                            closeMenu();
                            try {
                                await signalsPageStore.authStore.homeDashboardStore.removeOrgHomeDashboardId();
                                currentDashboard.setIsOrgHomeDashboard(false);
                                navigate(
                                    `/app/signals/${currentDashboard.route}/${currentDashboard.id}`,
                                );
                            } catch {
                                signalsPageStore.messageStore.logError(
                                    "Failed to remove org home dashboard",
                                );
                            }
                        },
                    },
                    label: "Remove as org home",
                    icon: <HomeWorkRounded />,
                });
            }

            menuItems.push("Divider");

            menuItems.push({
                id: "share-dashboard",
                props: {
                    onClick: () => {
                        setShareDialogOpen(true);
                        closeMenu();
                    },
                },
                label: "Share",
                icon: <Share />,
            });

            addEmailMenuItem();

            menuItems.push({
                id: "signals-dashboard-present",
                props: {
                    onClick: () => {
                        currentDashboard.setIsPresentationMode(true);
                        closeMenu();
                    },
                },
                label: "Present",
                icon: <PlayCircleOutlineRounded />,
            });

            menuItems.push({
                id: "signals-dashboard-save-as-PDF",
                props: {
                    onClick: () => {
                        setSavePDFOpen(true);
                        closeMenu();
                    },
                },
                label: "Save as PDF",
                icon: <DownloadRounded />,
                lastItemOfSection: canDeleteDashboard,
            });

            if (canDeleteDashboard) {
                menuItems.push({
                    id: "signals-dashboard-delete",
                    props: {
                        onClick: () => {
                            setShowConfirmDelete(true);
                            closeMenu();
                        },
                    },
                    label: (
                        <span style={{ color: theme.palette.error.main }}>
                            Delete
                        </span>
                    ),
                    icon: (
                        <DeleteRounded
                            sx={{ color: theme.palette.error.main }}
                        />
                    ),
                });
            }
        }

        return (
            <>
                <IconButton
                    id={"signals-more-icon-header"}
                    style={{
                        color: theme.palette.black.main,
                        border: "1px solid",
                        borderColor: theme.palette.gray.light,
                        borderRadius: theme.shape.borderRadius,
                        backgroundColor: theme.palette.white.main,
                    }}
                    onClick={() => setMenuOpen(true)}
                    size="small"
                    ref={ref}
                >
                    <MoreHoriz />
                </IconButton>
                <AcxMenu
                    open={menuOpen}
                    anchorElement={menuOpen ? ref.current : undefined}
                    onMenuClose={closeMenu}
                    menuItems={menuItems}
                />
                {/* Dialogs */}
                {canDeleteDashboard && (
                    <DashboardConfirmDelete
                        open={showConfirmDelete}
                        setOpen={setShowConfirmDelete}
                    />
                )}
                <DashboardSharingDialog
                    open={shareDialogOpen}
                    setOpen={setShareDialogOpen}
                />
                <DashboardScheduleEmailDialog
                    open={showEmailDialog}
                    setOpen={setShowEmailDialog}
                />
                <AcxDialog
                    isOpen={showConfirmUnsubscribe}
                    onClose={() => setShowConfirmUnsubscribe(false)}
                    title="Unsubscribe from Dashboard"
                    subTitle="Are you sure you would like to unsubscribe from your dashboard email?"
                    children={
                        <AcxButton
                            onClick={handleUnsubscribe}
                            loading={unsubscribeLoading}
                        >
                            Confirm
                        </AcxButton>
                    }
                />
                <DashboardPdfDialog
                    open={savePDFOpen}
                    setOpen={setSavePDFOpen}
                    toPDF={toPDF}
                />
                <DashboardDuplicateDialog
                    open={showDupEdit}
                    setOpen={setShowDupEdit}
                />
            </>
        );
    },
);
